import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Plus, Trash2, Edit2, Check, X, Video, Upload, Play } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminVideos = () => {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingVideo, setEditingVideo] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [formData, setFormData] = useState({
    title: '',
    video_url: '',
    thumbnail_url: '',
    is_active: true
  });

  useEffect(() => {
    fetchVideos();
  }, []);

  const fetchVideos = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/featured-videos`);
      setVideos(response.data);
    } catch (error) {
      console.error('Error fetching videos:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (video = null) => {
    if (video) {
      setEditingVideo(video);
      setFormData({
        title: video.title,
        video_url: video.video_url,
        thumbnail_url: video.thumbnail_url || '',
        is_active: video.is_active
      });
    } else {
      setEditingVideo(null);
      setFormData({
        title: '',
        video_url: '',
        thumbnail_url: '',
        is_active: true
      });
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingVideo(null);
    setUploadProgress(0);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleVideoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Check file size (max 100MB)
    if (file.size > 100 * 1024 * 1024) {
      alert('Video file must be less than 100MB');
      return;
    }

    console.log('Starting video upload:', file.name, file.size, file.type);
    setUploading(true);
    setUploadProgress(0);

    try {
      // Upload via backend endpoint
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);

      const uploadResponse = await axios.post(
        `${API}/upload-video`,
        formDataUpload,
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          },
          onUploadProgress: (progressEvent) => {
            const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            setUploadProgress(progress);
          }
        }
      );

      console.log('Upload successful:', uploadResponse.data);
      setFormData(prev => ({
        ...prev,
        video_url: uploadResponse.data.url,
        thumbnail_url: uploadResponse.data.thumbnail_url
      }));

    } catch (error) {
      console.error('Error uploading video:', error);
      console.error('Error details:', error.response?.data || error.message);
      const errorMsg = error.response?.data?.detail || error.message || 'Unknown error';
      alert(`Error uploading video: ${errorMsg}`);
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.video_url) {
      alert('Please upload a video first');
      return;
    }

    try {
      if (editingVideo) {
        await axios.put(`${API}/featured-videos/${editingVideo.id}`, formData);
      } else {
        await axios.post(`${API}/featured-videos`, formData);
      }
      handleCloseModal();
      fetchVideos();
    } catch (error) {
      console.error('Error saving video:', error);
      alert('Error saving video. Please try again.');
    }
  };

  const handleDelete = async (videoId) => {
    if (window.confirm('Are you sure you want to delete this video?')) {
      try {
        await axios.delete(`${API}/featured-videos/${videoId}`);
        fetchVideos();
      } catch (error) {
        console.error('Error deleting video:', error);
        alert('Error deleting video. Please try again.');
      }
    }
  };

  const handleActivate = async (videoId) => {
    try {
      await axios.post(`${API}/featured-videos/${videoId}/activate`);
      fetchVideos();
    } catch (error) {
      console.error('Error activating video:', error);
      alert('Error activating video. Please try again.');
    }
  };

  return (
    <div className="page-content">
      <section className="page-hero">
        <div className="container">
          <Link to="/admin/dashboard" className="btn-secondary" style={{ marginBottom: '16px', display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
            <ArrowLeft size={20} /> Back to Dashboard
          </Link>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '16px' }}>
            <div>
              <h1 className="display-large">Manage Featured Videos</h1>
              <p className="body-large">Upload and manage videos displayed on the homepage</p>
            </div>
            <button onClick={() => handleOpenModal()} className="btn-primary" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Plus size={20} /> Add Video
            </button>
          </div>
        </div>
      </section>

      <section style={{ padding: 'var(--spacing-xl) var(--spacing-medium)' }}>
        <div className="container">
          {loading ? (
            <p>Loading videos...</p>
          ) : videos.length === 0 ? (
            <div className="network-card" style={{ textAlign: 'center', padding: '48px' }}>
              <Video size={48} style={{ color: 'var(--text-light)', marginBottom: '16px' }} />
              <p>No featured videos yet. Add your first video!</p>
            </div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '24px' }}>
              {videos.map((video) => (
                <div key={video.id} className="network-card" style={{ padding: 0, overflow: 'hidden' }}>
                  <div style={{ position: 'relative' }}>
                    <video
                      src={video.video_url}
                      poster={video.thumbnail_url}
                      style={{
                        width: '100%',
                        aspectRatio: '16/9',
                        objectFit: 'cover',
                        background: '#000'
                      }}
                      muted
                      onMouseEnter={(e) => e.target.play()}
                      onMouseLeave={(e) => { e.target.pause(); e.target.currentTime = 0; }}
                    />
                    {video.is_active && (
                      <div style={{
                        position: 'absolute',
                        top: '12px',
                        right: '12px',
                        padding: '6px 12px',
                        background: 'var(--brand-primary)',
                        color: 'black',
                        borderRadius: '20px',
                        fontSize: '0.75rem',
                        fontWeight: 700,
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px'
                      }}>
                        <Play size={12} /> ACTIVE
                      </div>
                    )}
                  </div>
                  <div style={{ padding: '16px' }}>
                    <h3 style={{ margin: '0 0 8px', fontSize: '1.125rem' }}>{video.title}</h3>
                    <p style={{ margin: '0 0 16px', fontSize: '0.875rem', color: 'var(--text-light)' }}>
                      Added: {new Date(video.created_at).toLocaleDateString()}
                    </p>
                    <div style={{ display: 'flex', gap: '8px' }}>
                      {!video.is_active && (
                        <button
                          onClick={() => handleActivate(video.id)}
                          style={{
                            flex: 1,
                            padding: '8px 16px',
                            background: 'var(--brand-primary)',
                            color: 'black',
                            border: 'none',
                            borderRadius: '8px',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '6px',
                            fontWeight: 600
                          }}
                        >
                          <Check size={16} /> Set Active
                        </button>
                      )}
                      <button
                        onClick={() => handleOpenModal(video)}
                        style={{
                          padding: '8px 16px',
                          background: 'rgba(255,255,255,0.1)',
                          color: 'white',
                          border: '1px solid var(--border-medium)',
                          borderRadius: '8px',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px'
                        }}
                      >
                        <Edit2 size={16} /> Edit
                      </button>
                      <button
                        onClick={() => handleDelete(video.id)}
                        style={{
                          padding: '8px 16px',
                          background: '#dc2626',
                          color: 'white',
                          border: 'none',
                          borderRadius: '8px',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px'
                        }}
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Add/Edit Modal */}
      {showModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.8)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '20px'
        }}>
          <div style={{
            background: 'var(--bg-secondary)',
            borderRadius: '16px',
            padding: '32px',
            maxWidth: '600px',
            width: '100%',
            maxHeight: '90vh',
            overflowY: 'auto',
            position: 'relative',
            border: '1px solid var(--border-medium)'
          }}>
            <button
              onClick={handleCloseModal}
              style={{
                position: 'absolute',
                top: '16px',
                right: '16px',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                color: 'var(--text-light)'
              }}
            >
              <X size={24} />
            </button>

            <h2 className="heading-2" style={{ marginBottom: '24px' }}>
              {editingVideo ? 'Edit Video' : 'Add New Video'}
            </h2>

            <form onSubmit={handleSubmit}>
              <div className="form-group" style={{ marginBottom: '16px' }}>
                <label>Video Title *</label>
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  required
                  placeholder="e.g., 2024 Season Highlights"
                />
              </div>

              <div className="form-group" style={{ marginBottom: '16px' }}>
                <label>Upload Video *</label>
                <div style={{
                  border: '2px dashed var(--border-medium)',
                  borderRadius: '12px',
                  padding: '24px',
                  textAlign: 'center',
                  background: 'rgba(0,0,0,0.2)',
                  position: 'relative'
                }}>
                  {formData.video_url ? (
                    <div>
                      <video
                        src={formData.video_url}
                        style={{ width: '100%', maxHeight: '200px', borderRadius: '8px', marginBottom: '12px' }}
                        controls
                      />
                      <p style={{ color: 'var(--text-light)', fontSize: '0.875rem' }}>Video uploaded successfully!</p>
                      <button
                        type="button"
                        onClick={() => setFormData(prev => ({ ...prev, video_url: '', thumbnail_url: '' }))}
                        style={{
                          marginTop: '8px',
                          padding: '8px 16px',
                          background: 'rgba(255,255,255,0.1)',
                          color: 'white',
                          border: '1px solid var(--border-medium)',
                          borderRadius: '6px',
                          cursor: 'pointer'
                        }}
                      >
                        Remove & Upload Different Video
                      </button>
                    </div>
                  ) : uploading ? (
                    <div>
                      <div style={{
                        width: '100%',
                        height: '8px',
                        background: 'rgba(255,255,255,0.1)',
                        borderRadius: '4px',
                        overflow: 'hidden',
                        marginBottom: '12px'
                      }}>
                        <div style={{
                          width: `${uploadProgress}%`,
                          height: '100%',
                          background: 'var(--brand-primary)',
                          transition: 'width 0.3s ease'
                        }} />
                      </div>
                      <p style={{ color: 'var(--brand-primary)', fontWeight: 600 }}>Uploading... {uploadProgress}%</p>
                    </div>
                  ) : (
                    <div>
                      <Upload size={48} style={{ color: 'var(--text-light)', marginBottom: '12px' }} />
                      <p style={{ marginBottom: '12px', color: 'var(--text-light)' }}>Click to upload or drag and drop</p>
                      <p style={{ fontSize: '0.75rem', color: 'var(--text-light)' }}>MP4, MOV, WebM (max 100MB)</p>
                      <input
                        type="file"
                        accept="video/*"
                        onChange={handleVideoUpload}
                        style={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          width: '100%',
                          height: '100%',
                          opacity: 0,
                          cursor: 'pointer'
                        }}
                      />
                    </div>
                  )}
                </div>
              </div>

              <div className="form-group" style={{ marginBottom: '24px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '12px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    name="is_active"
                    checked={formData.is_active}
                    onChange={handleInputChange}
                    style={{ width: '20px', height: '20px', accentColor: 'var(--brand-primary)' }}
                  />
                  <span>Set as active video on homepage</span>
                </label>
                <p style={{ fontSize: '0.75rem', color: 'var(--text-light)', marginTop: '4px', marginLeft: '32px' }}>
                  Only one video can be active at a time
                </p>
              </div>

              <div style={{ display: 'flex', gap: '12px' }}>
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={uploading || !formData.video_url}
                  style={{ flex: 1 }}
                >
                  {editingVideo ? 'Save Changes' : 'Add Video'}
                </button>
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="btn-secondary"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminVideos;
